import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Loader2, KeyRound, Eye, EyeOff, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

export default function ResetPassword() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [pwd, setPwd] = useState("");
  const [cf, setCf] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (pwd.length < 8) { toast.error("La password deve avere almeno 8 caratteri"); return; }
    if (pwd !== cf) { toast.error("Le password non coincidono"); return; }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pwd });
      setDone(true);
      toast.success("Password aggiornata");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 text-center">
        <div className="max-w-sm">
          <p className="text-slate-400">Link non valido. Richiedi un nuovo link di recupero password.</p>
          <button onClick={() => navigate("/admin/forgot")} className="mt-4 text-blue-400 underline">Recupera password</button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4">
      <div className="w-full max-w-sm fade-up">
        <div className="flex flex-col items-center mb-7">
          <div className="w-14 h-14 rounded-2xl bg-blue-600 grid place-items-center shadow-lg shadow-blue-600/40 mb-3">
            <KeyRound className="w-7 h-7 text-white" />
          </div>
          <h1 className="font-head text-2xl font-bold">Nuova password</h1>
          <p className="text-slate-500 text-sm mt-1">Scegli una nuova password per il tuo account.</p>
        </div>

        {done ? (
          <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-center" data-testid="reset-success">
            <CheckCircle2 className="w-12 h-12 text-emerald-400 mx-auto mb-3" />
            <p className="text-sm text-slate-300">Password aggiornata con successo. Ora puoi accedere.</p>
            <button onClick={() => navigate("/admin/login")} className="mt-5 w-full bg-blue-600 hover:bg-blue-500 py-3 rounded-xl font-semibold" data-testid="reset-go-login-btn">Vai al login</button>
          </div>
        ) : (
          <form onSubmit={submit} className="bg-slate-900 border border-slate-800 rounded-2xl p-6 space-y-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Nuova password</label>
              <div className="relative mt-2">
                <input type={show ? "text" : "password"} value={pwd} onChange={(e) => setPwd(e.target.value)} data-testid="reset-password-input"
                  className="w-full bg-slate-950 border-2 border-slate-600 rounded-xl px-3 py-3 pr-11 text-sm text-slate-100 focus:outline-none focus:border-blue-500" required />
                <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-lg text-slate-400 hover:text-slate-200">
                  {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wider text-slate-500">Conferma password</label>
              <input type={show ? "text" : "password"} value={cf} onChange={(e) => setCf(e.target.value)} data-testid="reset-confirm-input"
                className="w-full mt-2 bg-slate-950 border-2 border-slate-600 rounded-xl px-3 py-3 text-sm text-slate-100 focus:outline-none focus:border-blue-500" required />
            </div>
            <button type="submit" disabled={loading} data-testid="reset-submit-btn"
              className="w-full bg-blue-600 hover:bg-blue-500 disabled:opacity-60 py-3 rounded-xl font-semibold flex items-center justify-center gap-2">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : null} Reimposta password
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
